import { createContext, FC, ReactNode, useEffect, useContext, useState } from 'react'
import { Auth, Hub } from 'aws-amplify'
import * as Sentry from '@sentry/react'
import { PageLoader } from 'components/PageLoader'
import { MFAVerificationModal } from 'components/Auth/MFAVerificationModal/MFAVerificationModal'
import { MFA_TYPES } from 'utils/services/auth/auth.service'
import { Group, SUPER_ADMIN_GROUP, authService, ADMIN_GROUP, MARKETING_GROUP, MFAChallenge } from './services/auth'

const HUB_EVENTS = {
  SIGN_IN: 'signIn',
  SIGN_OUT: 'signOut',
  SIGN_IN_FAILURE: 'signIn_failure',
  SESSION_EXPIRED: 'sessionExpired',
}

export type Props = {
  children: ReactNode
}

type Context = {
  isAuthenticated: boolean
  isAdmin: boolean
  isSuperAdmin: boolean
  isMarketingTeamMember: boolean
  groups: Group[]
  signOut: () => Promise<void>
  signIn: (email: string, password: string) => Promise<void>
}

export const getCurrentUserEmail = async (): Promise<string | null> => {
  try {
    const cognitoUser = await Auth.currentAuthenticatedUser()

    return cognitoUser?.attributes?.email
  } catch (error) {
    console.error('An error occurred while trying to get current user email', error)
    return null
  }
}

export const AuthContext = createContext<Context>({
  isAuthenticated: false,
  isAdmin: false,
  isSuperAdmin: false,
  isMarketingTeamMember: false,
  groups: [],
  signIn: async () => void 0,
  signOut: async () => void 0,
})

export const AuthProvider: FC<Props> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [groups, setGroups] = useState<Group[]>([])
  const [mfaChallenge, setMfaChallenge] = useState<MFAChallenge | null>(null)

  const setSentryUser = async () => {
    try {
      const email = await getCurrentUserEmail()
      Sentry.setUser(email ? { email } : null)
    } catch (error) {
      console.error('An error occurred while trying to set user', error)
    }
  }

  const signOut = async (): Promise<void> => {
    try {
      await authService.signOut()
    } catch (error) {
      console.error('[Auth] signOut:', error)
    }
  }

  const initializeGroups = async () => {
    const groups = await authService.getUserGroups()
    setGroups(groups as Group[])
  }

  const completeSignIn = async () => {
    try {
      setSentryUser()
      setIsAuthenticated(true)
      await initializeGroups()
      setMfaChallenge(null)
    } catch (error) {
      console.error('An error occurred while trying to complete sign in', error)
      throw error
    }
  }

  const signIn = async (email: string, password: string): Promise<void> => {
    const result = await authService.signIn({ email: email.toLowerCase(), password })

    // Check if MFA is required
    if (result) {
      setMfaChallenge(result)
      return
    }

    // No MFA required, complete sign in
    await completeSignIn()
  }

  const handleMFASubmit = async (code: string): Promise<void> => {
    if (!mfaChallenge) {
      throw new Error('No MFA challenge found')
    }

    await authService.confirmMFA(mfaChallenge.user, code, mfaChallenge.type)
    await completeSignIn()
  }

  const handleMFAResend = async (): Promise<void> => {
    if (!mfaChallenge || !mfaChallenge.credentials) {
      throw new Error('No MFA challenge or credentials found')
    }

    try {
      const newUser = await authService.resendMFACode(mfaChallenge.credentials)
      // Update the challenge with the new user object
      setMfaChallenge({
        ...mfaChallenge,
        user: newUser,
      })
    } catch (error) {
      console.error('Failed to resend MFA code:', error)
      throw error
    }
  }

  useEffect(() => {
    ;(async () => {
      const isAuthenticated = await authService.checkIsAuthenticated()

      if (isAuthenticated) {
        await Promise.all([setSentryUser(), initializeGroups()])
      }

      setIsAuthenticated(isAuthenticated)
      setIsInitialized(true)
    })()

    Hub.listen('auth', async (data) => {
      switch (data.payload.event) {
        case HUB_EVENTS.SESSION_EXPIRED:
          authService.refreshToken()

          break
        case HUB_EVENTS.SIGN_OUT:
          setIsAuthenticated(false)
          setGroups([])
          Sentry.setUser(null)

          break
        default:
          break
      }
    })
  }, [])

  if (!isInitialized) {
    return <PageLoader />
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        signIn,
        signOut,
        groups,
        isMarketingTeamMember: groups.includes(MARKETING_GROUP),
        isAdmin: groups.includes(SUPER_ADMIN_GROUP) || groups.includes(ADMIN_GROUP),
        isSuperAdmin: groups.includes(SUPER_ADMIN_GROUP),
      }}
    >
      {children}
      {mfaChallenge && (
        <MFAVerificationModal
          mfaType={mfaChallenge.type}
          onSubmit={handleMFASubmit}
          onResend={mfaChallenge.type === MFA_TYPES.SMS ? handleMFAResend : undefined}
        />
      )}
    </AuthContext.Provider>
  )
}

export function useAuthContext() {
  const context = useContext(AuthContext)

  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }

  return context
}
